import { __rest } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useMemo } from 'react';
import { styled } from 'styled-components';
import { getErrorId, getInfoId } from '@hivebrite/utils';
import { Box } from '@design-system/box';
import { Label } from '@design-system/label';
import { BaseInput } from './BaseInput';
const InputComponent = ({ alignItems, label, labelStyle, }) => useMemo(() => forwardRef((_a, ref) => {
    var { ariaDescribedBy, ariaLabel, checked, dataTestId, details, disabled, error, id, info, labelMultiline, name, onChange, required, type, value } = _a, inputProps = __rest(_a, ["ariaDescribedBy", "ariaLabel", "checked", "dataTestId", "details", "disabled", "error", "id", "info", "labelMultiline", "name", "onChange", "required", "type", "value"]);
    const hasError = Boolean(error);
    const errorId = useMemo(() => getErrorId(id), [id]);
    const hasInfo = Boolean(info);
    const infoId = useMemo(() => getInfoId(id), [id]);
    return (_jsxs(Box, { flexDirection: "column", children: [_jsxs(Box, { alignItems: alignItems !== null && alignItems !== void 0 ? alignItems : 'center', children: [_jsx(StyledRadio, Object.assign({}, ((hasError || ariaDescribedBy) && {
                        'aria-describedby': [
                            hasError && errorId,
                            hasInfo && infoId,
                            ariaDescribedBy,
                        ]
                            .filter(Boolean)
                            .join(' '),
                    }), (hasError && {
                        'aria-invalid': hasError,
                    }), (ariaLabel && {
                        'aria-label': ariaLabel,
                    }), { "aria-checked": Boolean(checked), "aria-required": required ? 'true' : undefined, checked: checked, "data-testid": dataTestId, disabled: disabled, id: id, name: name, onChange: () => onChange(value), ref: ref, type: type, value: value }, inputProps)), _jsx(Label, { disabled: disabled, fontWeight: 400, id: id, multiline: labelMultiline, position: "right", required: required, wrapperProps: {
                            style: Object.assign({ display: 'flex', flex: '1 1 100%', paddingTop: '1px' }, labelStyle),
                        }, children: label })] }), details && (_jsx(Details, { font: "metadata-regular-dove", ml: "26px", mt: 3, onClick: () => !disabled && onChange(value), children: details }))] }));
}), [alignItems, label, labelStyle]);
export const RadioInput = (_a) => {
    var { alignItems, disabled = false, label, labelStyle, name, variant = 'normal', width = '100%' } = _a, otherProps = __rest(_a, ["alignItems", "disabled", "label", "labelStyle", "name", "variant", "width"]);
    return (_jsx(BaseInput, Object.assign({}, otherProps, { additionalInformationMl: "26px", disabled: disabled, inputComponent: InputComponent({ alignItems, label, labelStyle }), name: name, type: "radio", variant: variant, width: width })));
};
const StyledRadio = styled.input `
  background-color: ${({ theme }) => theme.checkbox.background.unchecked};
  border: 1px solid ${({ theme }) => theme.checkbox.border.color};
  border-radius: 10px;
  cursor: pointer;
  display: inline-block;
  height: 18px;
  margin: 0 !important;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  ${({ disabled }) => disabled && `pointer-events: none;`}
  position: relative;
  width: 18px;
  flex-shrink: 0;
  -webkit-appearance: none;
  // To avoid zoom on iOS mobile devices, inputs must have font-size set to 16px (font-size is not really used here, no need to scope to only s & m)
  font-size: ${({ theme }) => theme.fontSizes[3]} !important;

  &:after {
    background-color: transparent;
    border-radius: 25px;
    content: '';
    display: block;
    height: 10px;
    left: 3px;
    position: absolute;
    top: 3px;
    width: 10px;
  }
  &:checked:after {
    background-color: ${({ theme }) => theme.checkbox.background.checked};
  }
`;
const Details = styled(Box) `
  cursor: pointer;
`;
//# sourceMappingURL=RadioInput.js.map